import { useQuery } from '@tanstack/react-query';
import { PkBox } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import type { MouseEvent } from 'react';
import { useMemo, memo } from 'react';

import StandingTable from '../../components/standings/StandingTable';
import { getStandingsQuery } from '../queries/standings';
import { useTranslateFn } from '../Translate';

import { columns } from './columns';

import { trackCardElement } from '@services/GoogleTagManagerService';
import type { Match } from '@shared-types/match.types';
import { MatchStatus, TeamCategory } from '@shared-types/match.types';
import type { StandingsOptions } from '@shared-types/standing.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { IS_TEST } from '@utilities/browser';
import { getRefreshRate } from '@utilities/live';
import { competitionUrl } from '@utilities/WindowContext';

import * as classes from './styles.module.scss';

const Standings = ({
  options,
  groupId,
  newTableFormat,
  enableFormguide,
  boxed = false,
}: {
  options: { interval?: number; match: Match };
  groupId: string;
  newTableFormat: boolean;
  enableFormguide?: boolean;
  boxed?: boolean;
}) => {
  const t = useTranslateFn();
  const interval = options?.interval;
  const match = options?.match;

  const pollInterval = useMemo(() => (interval < 0 ? interval : getRefreshRate(match)), [interval, match]);

  const { data: standing } = useQuery({
    ...getStandingsQuery({ groupIds: groupId }),
    select: (data) => {
      const res = data?.at(0);
      if (!newTableFormat || !res) return res;

      return {
        ...res,
        items: res?.items?.filter((s) => s.team.id === match.awayTeam?.id || s.team.id === match.homeTeam?.id),
      };
    },
    refetchInterval: !IS_TEST && pollInterval,
  });

  const standingsOptions: StandingsOptions = useMemo(
    () => ({
      cup: match?.competition?.id,
      season: match?.seasonYear,
      groupId,
      enableFormguide,
      isClub: match?.competition?.teamCategory === TeamCategory.CLUB,
      columns,
      removeHeading: newTableFormat,
      newTableFormat,
      enableRanking:
        +(match?.matchday?.sequenceNumber ?? 0) > 1 || [MatchStatus.LIVE, MatchStatus.FINISHED].includes(match?.status),
    }),
    [
      enableFormguide,
      groupId,
      match?.competition?.id,
      match?.competition?.teamCategory,
      match?.seasonYear,
      newTableFormat,
      match?.matchday?.sequenceNumber,
      match?.status,
    ],
  );

  const trackingData = useMemo(() => {
    if (!standing?.group) return null;

    return {
      id: match.round?.id,
      name: newTableFormat ? 'Table - 2 teams' : standing.group.metaData.groupName,
      category: newTableFormat ? '2-teams league table' : 'standings',
    };
  }, [match.round?.id, newTableFormat, standing?.group]);

  const handleLinkClick = (e: MouseEvent) => {
    if (e.button > 1) return; // ignore right-clicks (context menu)
    const data = { ...trackingData, variant: 'open full table' };
    GoogleTagManagerHelper.gtmCardElementClick(e.target as HTMLElement, data);
  };

  if (!standing) return null;

  return (
    <div
      className={clsx(newTableFormat && classes.filtered, !boxed && 'pk-p--m')}
      ref={trackCardElement(trackingData, true)}
    >
      {newTableFormat && (
        <div className='pk-mb--m pk-d--flex pk-justify-content--between pk-align-items--center'>
          <h3 className='pk-font-size--l'>{t('table')}</h3>
          <a
            href={`/${competitionUrl}/standings/`}
            className='pk-font-size--s pk-text--interaction pk-font--base-semibold'
            title={t('view_all')}
            onClick={handleLinkClick}
            onAuxClick={handleLinkClick}
          >
            {t('view_all')}
          </a>
        </div>
      )}
      {boxed ? (
        <PkBox className={clsx('pk-mb--l', classes.standingsBox)} rounded='xl'>
          <StandingTable {...standingsOptions} groupStanding={standing} tracking={trackingData} />
        </PkBox>
      ) : (
        <StandingTable {...standingsOptions} groupStanding={standing} tracking={trackingData} />
      )}
    </div>
  );
};

export default memo(Standings);
